import {MergeView} from '@codemirror/merge'
import {useEffect, useMemo, useRef, type RefObject} from 'react'
import {Compartment} from '@codemirror/state'
import type {CodeMirrorDiffProps} from '../CodeMirrorDiff'
import {loadLanguage} from './load-language'
import {basicSetup} from '../extensions/basic-setup'
import {spacingControls} from '../extensions/spacing-controls'
import {secretDetectedListener} from '../extensions/secret-detected'
import {getTheme} from '../themes/github-theme'
import {scrollerAttributes} from '../extensions/scroller-attributes'
import {EditorView} from '@codemirror/view'
import {readOnly} from '../extensions/readonly'

interface UseCodeMirrorDiff extends CodeMirrorDiffProps {
  parentRef: RefObject<HTMLElement | null>
}

export function useCodeMirrorDiff({
  fileName,
  left,
  leftLabelledBy,
  parentRef,
  right,
  rightLabelledBy,
  spacing,
}: UseCodeMirrorDiff) {
  const mergeViewRef = useRef<MergeView | null>(null)

  const autoCompleteCompartment = useMemo(() => {
    return new Compartment()
  }, [])
  const languageCompartment = useMemo(() => {
    return new Compartment()
  }, [])
  const themeCompartment = useMemo(() => {
    return new Compartment()
  }, [])

  const [managedLeftExtensions, managedRightExtensions] = useMemo(() => {
    const exts = [
      ...basicSetup,
      ...spacingControls(spacing),
      secretDetectedListener(),
      languageCompartment.of([]),
      autoCompleteCompartment.of([]),
      themeCompartment.of([getTheme()]),
      readOnly(),
    ]

    const leftExts = [
      scrollerAttributes(leftLabelledBy),
      EditorView.contentAttributes.of({
        'aria-labelledby': leftLabelledBy,
      }),
    ]
    const rightExts = [
      scrollerAttributes(rightLabelledBy),
      EditorView.contentAttributes.of({
        'aria-labelledby': rightLabelledBy,
      }),
    ]

    return [exts.concat(leftExts), exts.concat(rightExts)]
  }, [spacing, languageCompartment, autoCompleteCompartment, themeCompartment, leftLabelledBy, rightLabelledBy])

  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return
    mergeViewRef.current = new MergeView({
      a: {
        doc: left,
        extensions: managedLeftExtensions,
      },
      b: {
        doc: right,
        extensions: managedRightExtensions,
      },
      parent,
      gutter: false,
    })

    loadLanguage({
      autoCompleteCompartment,
      fileName,
      languageCompartment,
      managedExtensions: [],
      themeCompartment,
      view: mergeViewRef.current?.a,
      viewB: mergeViewRef.current?.b,
    })

    return () => mergeViewRef.current?.destroy()
    // Needs to run only once and destruct only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
